import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, X, UserCircle2, ArrowUp, PartyPopper, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchProgreso } from "../lib/profile";

const COACH_KEY = "cl_onboarding_coach_seen";
const PENDING_KEY = "cl_onboarding_pending";
const CELEBRATED_KEY = "cl_onboarding_celebrated";

function lsGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, val); } catch { /* noop */ }
}

/**
 * OnboardingNudge — guía de primer uso para completar el Perfil.
 * 1) Banner llamativo (persistente) mientras el perfil esté incompleto.
 * 2) Coach mark (flecha) apuntando al menú de perfil (arriba a la derecha), 1ª vez.
 * 3) Al completar el 100%, muestra un mensaje de felicitación (una sola vez).
 */
export default function OnboardingNudge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [porcentaje, setPorcentaje] = useState(null);
  const [bannerHidden, setBannerHidden] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [coachSeen, setCoachSeen] = useState(() => lsGet(COACH_KEY) === "1");

  useEffect(() => {
    if (!user || user === false || user.role === "admin") return;
    let alive = true;
    fetchProgreso()
      .then((p) => {
        if (!alive) return;
        const pct = typeof p?.porcentaje === "number" ? p.porcentaje : null;
        setPorcentaje(pct);
        if (pct === null) return;
        if (pct < 100) {
          lsSet(PENDING_KEY, "1");
        } else if (lsGet(PENDING_KEY) === "1" && lsGet(CELEBRATED_KEY) !== "1") {
          setCelebrate(true);
          lsSet(CELEBRATED_KEY, "1");
          try { localStorage.removeItem(PENDING_KEY); } catch { /* noop */ }
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    if (!celebrate) return;
    const t = setTimeout(() => setCelebrate(false), 8000);
    return () => clearTimeout(t);
  }, [celebrate]);

  if (!user || user === false || user.role === "admin") return null;

  // Mensaje de felicitación al completar el perfil (una sola vez).
  if (celebrate) {
    return (
      <div
        className="mb-6 relative overflow-hidden rounded-2xl p-5 text-white shadow-lifted"
        style={{ background: "linear-gradient(110deg, #059669 0%, #10B981 55%, #34D399 100%)" }}
        data-testid="onboarding-celebrate"
      >
        <div className="absolute -top-8 -right-6 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
            <PartyPopper size={24} />
          </span>
          <div className="flex-1">
            <p className="font-extrabold text-lg tracking-tight">¡Perfil completo! 🎉</p>
            <p className="text-sm text-white/90 mt-0.5">
              Excelente, {user.name?.split(" ")[0] || "constructor"}. Ya estás listo para aprovechar todo el Ecosistema ConstruLink.
            </p>
          </div>
          <button
            onClick={() => setCelebrate(false)}
            data-testid="onboarding-celebrate-close"
            aria-label="Cerrar"
            className="w-9 h-9 rounded-lg text-white/80 hover:bg-white/15 flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    );
  }

  const incompleto = porcentaje !== null && porcentaje < 100;
  if (!incompleto) return null;

  const cerrarCoach = () => { lsSet(COACH_KEY, "1"); setCoachSeen(true); };
  const irAlPerfil = () => { cerrarCoach(); navigate("/mi-perfil/global"); };

  return (
    <>
      {/* 1) Banner llamativo (fondo oscuro corporativo + CTA roja) */}
      {!bannerHidden && (
        <div
          className="mb-6 relative overflow-hidden rounded-2xl p-5 sm:p-6 text-white shadow-lifted ring-1 ring-white/10"
          style={{ background: "linear-gradient(115deg, #0B1E36 0%, #12305A 55%, #1E3A8A 100%)" }}
          data-testid="onboarding-banner"
        >
          {/* Glows decorativos */}
          <div className="absolute -top-10 -right-8 w-48 h-48 rounded-full bg-brand-red/25 blur-3xl" />
          <div className="absolute -bottom-16 left-10 w-48 h-48 rounded-full bg-brand-blue/30 blur-3xl" />

          <div className="relative flex items-start sm:items-center gap-4 flex-col sm:flex-row">
            <span className="w-12 h-12 rounded-2xl bg-white/15 text-white flex items-center justify-center shrink-0 ring-1 ring-white/20">
              <UserCircle2 size={24} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-amber-300">
                <Sparkles size={12} /> Da el primer paso
              </p>
              <p className="mt-1 font-extrabold text-lg sm:text-xl tracking-tight">
                ¡Bienvenido, {user.name?.split(" ")[0] || "constructor"}! Completa tu perfil
              </p>
              <p className="text-sm text-white/80 mt-0.5">
                Un perfil completo genera más confianza y oportunidades en el Ecosistema ConstruLink.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 max-w-xs h-2.5 rounded-full bg-white/15 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${porcentaje}%`, background: "linear-gradient(90deg, #F59E0B, #EF4444)" }}
                    data-testid="onboarding-progress-bar"
                  />
                </div>
                <span className="text-xs font-extrabold text-amber-300">{porcentaje}%</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
              <button
                onClick={irAlPerfil}
                data-testid="onboarding-completar-btn"
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-brand-red text-white font-bold text-sm hover:bg-red-600 shadow-lg shadow-brand-red/30 hover:scale-[1.03] transition-all"
              >
                Completar mi perfil <ArrowRight size={16} />
              </button>
              <button
                onClick={() => setBannerHidden(true)}
                data-testid="onboarding-banner-close"
                aria-label="Cerrar aviso"
                className="w-9 h-9 rounded-lg text-white/70 hover:bg-white/15 flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2) Coach mark: flecha + tooltip apuntando al menú de perfil (arriba a la derecha) */}
      {!coachSeen && (
        <div className="fixed top-[70px] right-3 sm:right-5 z-[60] w-[270px] animate-fade-up" data-testid="onboarding-coachmark">
          <ArrowUp size={26} className="text-brand-red ml-auto mr-6 -mb-1 animate-bounce" />
          <div className="rounded-2xl bg-brand-navy text-white shadow-xl p-4 ring-1 ring-white/10">
            <p className="text-sm font-bold">Aquí está tu perfil 👆</p>
            <p className="text-xs text-white/80 mt-1 leading-relaxed">
              Haz clic en tu avatar (arriba a la derecha) y entra a <span className="font-semibold">"Mi Perfil"</span> para completar tus datos.
            </p>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={cerrarCoach}
                data-testid="onboarding-coach-entendido"
                className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-semibold"
              >
                Entendido
              </button>
              <button
                onClick={irAlPerfil}
                data-testid="onboarding-coach-ir"
                className="px-3 py-1.5 rounded-lg bg-brand-red hover:bg-red-600 text-xs font-semibold"
              >
                Ir ahora
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
